import { useMemo, useState } from 'react';
import { Box, Modal, Typography } from '@mui/material';

import logo from '@assets/svg/logo.svg';
import mail from '@assets/svg/mail.svg';
import mail_red from '@assets/svg/mail_red.svg';
import mail_filled from '@assets/svg/mail_filled.svg';

import QuizButton, { ButtonType } from '../common/QuizButton';
import { APP_CONSTANTS, EMAIL_REGEX } from '@utils/constants';
import { getParamByKey } from '@utils/common';

function Unsubscribe() {
  const [email, setEmail] = useState(() => getParamByKey('email') ?? '');
  const [error, setError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isDisabled = useMemo(() => email.trim() === '', [email]);

  const handleCancel = () => {
    const isValid = email.toLowerCase().match(EMAIL_REGEX);

    if (isValid) {
      setIsModalOpen(true);
    } else {
      setError(true);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: '#FCF4EF',
        width: '100%',
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          maxWidth: '460px',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          margin: 'auto',
          padding: '0 20px',
        }}
      >
        <img
          draggable={false}
          src={logo}
          width={72}
          height={72}
          alt="logo"
          style={{ margin: '30px auto 0' }}
        />

        <Box
          sx={{
            width: '100%',
            margin: '20px 0 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Avenir500',
              fontSize: '15px',
              lineHeight: '20px',
              color: '#ED744E',
              textAlign: 'center',
              userSelect: 'none',
            }}
          >
            Terminate subscription
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Avenir600',
              fontSize: '24px',
              lineHeight: '32px',
              color: '#282624',
              textAlign: 'center',
              userSelect: 'none',
            }}
          >
            To cancel your subscription - please use the email used to purchase
          </Typography>
        </Box>

        <Box
          sx={{
            width: '100%',
            margin: '30px 0 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              '& img': {
                position: 'absolute',
                top: '22px',
                left: '20px',
              },
              '& input': {
                boxSizing: 'border-box',
                width: '100%',
                background: 'white',
                border: `1px solid ${error ? '#E03045' : '#F0CCB9'}`,
                borderRadius: '12px',
                color: '#282624',
                fontFamily: 'Avenir500',
                fontSize: '16px',
                height: '60px',
                paddingLeft: '60px',
                '&:focus-visible': {
                  outline: 'none',
                },
              },
              '& input::placeholder': {
                color: '#908C89',
                fontFamily: 'Avenir400',
                fontSize: '16px',
                lineHeight: '22px',
              },
            }}
          >
            <img src={error ? mail_red : mail} alt="mail" />
            <input
              placeholder="Enter your email"
              type="email"
              value={email}
              onChange={(e) => {
                setError(false);
                setEmail(e.target.value);
              }}
            />
            {error && (
              <Typography
                sx={{
                  color: '#E03045',
                  fontFamily: 'Avenir400',
                  fontSize: '14px',
                  marginTop: '10px',
                }}
              >
                Please enter a valid email
              </Typography>
            )}
          </Box>

          <QuizButton
            text="Cancel subscription"
            variant={ButtonType.ORANGE}
            onClick={handleCancel}
            disabled={isDisabled}
          />
        </Box>

        <Box
          sx={{
            width: '100%',
            margin: 'auto 0 0',
            padding: '20px 0',
            borderTop: '1px solid rgba(240, 204, 185, 0.4)',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Avenir400',
              fontSize: '14px',
              lineHeight: '19px',
              textAlign: 'center',
              color: '#282624',
              '& a': { color: '#ED744E', textDecoration: 'underline' },
            }}
          >
            Have a question? Reach our friendly support team{' '}
            <a href={`mailto:${APP_CONSTANTS.EMAIL}`}>here</a>
          </Typography>
        </Box>
      </Box>

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        slotProps={{
          backdrop: { sx: { backgroundColor: 'rgba(40, 38, 36, 0.6)' } },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'calc(100% - 40px)',
            maxWidth: '335px',
            backgroundColor: '#FFF',
            borderRadius: '20px',
            padding: '30px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            outline: 'none',
          }}
        >
          <img src={mail_filled} width={80} height={80} alt="" />
          <Typography
            sx={{
              margin: '20px 0 0',
              fontFamily: 'Avenir600',
              fontSize: '24px',
              lineHeight: '32px',
              color: '#282624',
              textAlign: 'center',
            }}
          >
            Subscription cancelled
          </Typography>
          <Typography
            sx={{
              margin: '10px 0 0',
              fontFamily: 'Avenir400',
              fontSize: '16px',
              lineHeight: '22px',
              color: '#908C89',
              textAlign: 'center',
            }}
          >
            We&apos;re sorry to see you go. You can keep using your plan until
            the end of your current billing period.
          </Typography>
          <Box sx={{ width: '100%', margin: '30px 0 0' }}>
            <QuizButton
              text="OK"
              variant={ButtonType.ORANGE}
              onClick={() => setIsModalOpen(false)}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

export default Unsubscribe;
